/**
 * "Invio Blagomail": la rete di consegna. Il cliente attiva il proprio dominio,
 * incolla 2-3 record DNS, e da lì le sue email partono da un'infrastruttura con
 * reputazione consolidata — firmate col suo dominio. In più: conferme di lettura.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow, format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  Rocket, ShieldCheck, Copy, RefreshCw, Loader2, Eye, MailCheck,
  AlertTriangle, Send, CircleDashed, Gauge,
} from 'lucide-react';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  accountsApi, deliveryApi,
  type SendingDomain, type DeliveryMessage,
} from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';

const STATUS_META: Record<DeliveryMessage['status'], { label: string; cls: string; icon: typeof Send }> = {
  sent: { label: 'Inviata', cls: 'text-muted-foreground', icon: Send },
  deferred: { label: 'In coda', cls: 'text-amber-600', icon: CircleDashed },
  delivered: { label: 'Consegnata', cls: 'text-emerald-600', icon: MailCheck },
  opened: { label: 'Aperta', cls: 'text-indigo-600', icon: Eye },
  clicked: { label: 'Cliccata', cls: 'text-indigo-600', icon: Eye },
  bounced: { label: 'Rimbalzata', cls: 'text-destructive', icon: AlertTriangle },
  spam: { label: 'Segnata spam', cls: 'text-destructive', icon: AlertTriangle },
};

export function DeliveryPage() {
  const qc = useQueryClient();
  const domainsQuery = useQuery({ queryKey: ['delivery-domains'], queryFn: () => deliveryApi.domains() });
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => accountsApi.list() });
  const msgQuery = useQuery({ queryKey: ['delivery-messages'], queryFn: () => deliveryApi.messages(50) });

  const domains = domainsQuery.data?.domains ?? [];
  const available = domainsQuery.data?.available ?? false;
  const accounts = accountsQuery.data?.accounts ?? [];
  const stats = msgQuery.data?.stats;

  // caselle il cui dominio non è ancora nella rete
  const activatable = accounts.filter(
    (a) => !domains.some((d) => d.domain === a.email_address.split('@')[1]?.toLowerCase())
  );

  const activateMut = useMutation({
    mutationFn: (accountId: string) => deliveryApi.activate(accountId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['delivery-domains'] });
      toast.success('Dominio registrato: ora inserisci i record DNS');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Attivazione fallita'),
  });

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-8 py-8 space-y-6">

          <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white p-6">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
                <Rocket className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold leading-tight">Invio Blagomail</h1>
                <p className="text-sm text-white/85 mt-1 max-w-lg">
                  Le tue email partono dalla nostra infrastruttura di consegna, firmate col tuo dominio:
                  meno spam, più recapito. E scopri quando vengono aperte.
                </p>
              </div>
            </div>
            {stats && stats.sent > 0 && (
              <div className="mt-5 grid grid-cols-4 gap-2">
                {[
                  { v: stats.sent, l: 'inviate (30gg)' },
                  { v: stats.delivered, l: 'consegnate' },
                  { v: `${stats.open_rate}%`, l: 'aperte' },
                  { v: `${stats.score}/100`, l: 'punteggio' },
                ].map((c) => (
                  <div key={c.l} className="bg-white/12 rounded-lg py-2.5 text-center">
                    <div className="text-xl font-bold">{c.v}</div>
                    <div className="text-[11px] text-white/75">{c.l}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {!available && (
            <Card className="border-amber-200 bg-amber-50/40">
              <CardContent className="pt-5 text-sm">
                La rete di invio non è ancora configurata sul server (mancano le credenziali Brevo).
                Contatta Blagoweb per attivarla.
              </CardContent>
            </Card>
          )}

          {/* Domini da attivare */}
          {available && activatable.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Attiva una casella</CardTitle>
                <CardDescription>Registriamo il tuo dominio e ti diamo i record DNS da inserire.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {activatable.map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded-md border p-3">
                    <div className="text-sm">
                      <div className="font-medium">{a.email_address}</div>
                      <div className="text-xs text-muted-foreground">dominio: {a.email_address.split('@')[1]}</div>
                    </div>
                    <Button size="sm" onClick={() => activateMut.mutate(a.id)} disabled={activateMut.isPending}>
                      {activateMut.isPending && activateMut.variables === a.id
                        ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        : <Rocket className="h-4 w-4 mr-2" />}
                      Attiva
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {domains.map((d) => <DomainCard key={d.id} domain={d} />)}

          {/* Registro consegne / conferme di lettura */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Gauge className="h-4 w-4 text-indigo-500" /> Registro consegne
              </CardTitle>
              <CardDescription>Cosa è successo davvero alle tue email dopo l'invio.</CardDescription>
            </CardHeader>
            <CardContent>
              {(msgQuery.data?.messages ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Ancora nessun invio tracciato. Le email inviate da qui in poi compariranno qui.
                </p>
              ) : (
                <div className="space-y-1">
                  {msgQuery.data!.messages.map((m) => {
                    const meta = STATUS_META[m.status];
                    const Icon = meta.icon;
                    return (
                      <div key={m.id} className="flex items-center gap-3 rounded-md border px-3 py-2">
                        <Icon className={cn('h-4 w-4 shrink-0', meta.cls)} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">
                            <span className="font-medium">{m.to_email}</span>
                            <span className="text-muted-foreground"> · {m.subject || '(senza oggetto)'}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            <span className={meta.cls}>{meta.label}</span>
                            {m.first_opened_at && ` · aperta ${m.open_count > 1 ? `${m.open_count} volte, l'ultima ` : ''}${formatDistanceToNow(new Date(m.first_opened_at), { addSuffix: true, locale: it })}`}
                            {m.bounce_reason && ` · ${m.bounce_reason}`}
                            {m.via === 'network' && ' · via rete Blagomail'}
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {format(new Date(m.sent_at), 'd MMM HH:mm', { locale: it })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function DomainCard({ domain: d }: { domain: SendingDomain }) {
  const qc = useQueryClient();
  const [checking, setChecking] = useState(false);

  const toggleMut = useMutation({
    mutationFn: (enabled: boolean) => deliveryApi.toggle(d.domain, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['delivery-domains'] }),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Operazione fallita'),
  });

  async function check() {
    setChecking(true);
    try {
      const r = await deliveryApi.check(d.domain);
      qc.invalidateQueries({ queryKey: ['delivery-domains'] });
      toast[r.domain.brevo_authenticated ? 'success' : 'message'](
        r.domain.brevo_authenticated
          ? 'Dominio autenticato: puoi accendere la rete!'
          : 'Record non ancora visibili. Il DNS può metterci fino a qualche ora.'
      );
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Verifica fallita');
    } finally {
      setChecking(false);
    }
  }

  const ready = d.brevo_authenticated;

  return (
    <Card className={cn(ready && d.enabled && 'border-emerald-200')}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              {d.domain}
              {ready
                ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Autenticato</span>
                : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">In attesa del DNS</span>}
            </CardTitle>
            <CardDescription>
              {ready
                ? (d.enabled ? 'Le email di questo dominio partono dalla rete Blagomail.' : 'Pronto: accendi l\'interruttore per usarlo.')
                : 'Inserisci i record qui sotto nel pannello DNS del dominio, poi premi Verifica.'}
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Switch
              checked={d.enabled}
              disabled={!ready || toggleMut.isPending}
              onCheckedChange={(v) => toggleMut.mutate(v)}
            />
            <span className="text-[11px] text-muted-foreground">{d.enabled ? 'ATTIVO' : 'spento'}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {d.dns_records.length > 0 && !ready && (
          <div className="space-y-2">
            {d.dns_records.map((r, i) => (
              <div key={i} className="rounded-md border bg-muted/30 p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold">{r.label}</span>
                  {r.ok
                    ? <span className="text-[11px] text-emerald-600 inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> trovato</span>
                    : <span className="text-[11px] text-muted-foreground">da inserire</span>}
                </div>
                <div className="grid grid-cols-[70px_1fr] gap-x-3 gap-y-1 text-xs font-mono">
                  <span className="text-muted-foreground">Tipo</span><span>{r.type}</span>
                  <span className="text-muted-foreground">Nome</span><span className="break-all">{r.host}</span>
                  <span className="text-muted-foreground">Valore</span>
                  <span className="break-all flex items-start gap-1">
                    <span className="flex-1">{r.value}</span>
                    <button
                      onClick={() => { void navigator.clipboard.writeText(r.value); toast.success('Valore copiato'); }}
                      className="text-muted-foreground hover:text-foreground shrink-0"
                      title="Copia"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {d.last_error && <p className="text-xs text-destructive">{d.last_error}</p>}

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void check()} disabled={checking}>
            {checking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Verifica DNS
          </Button>
          {d.last_checked_at && (
            <span className="text-xs text-muted-foreground">
              ultimo controllo {formatDistanceToNow(new Date(d.last_checked_at), { addSuffix: true, locale: it })}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
